import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isEditor } from "@/lib/roles";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Landmark, ExternalLink, MapPin, ArrowRight, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_IMG = "https://images.unsplash.com/photo-1557635919-8ece988c2b5b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";
const BLANK = {
  name: "", jurisdiction: "", website: "", image_url: "", efiling: "", efiling_url: "", efiling_notes: "",
  address: "", hours: "", clerk_contact: "", filing_fees: "", service_rules: "", parking: "", security: "",
};
const FIELDS = [
  ["name", "Court Name"], ["jurisdiction", "Jurisdiction"], ["website", "Official Website URL"], ["image_url", "Image URL"],
  ["efiling", "E-Filing Summary"], ["efiling_url", "E-Filing Portal URL"], ["address", "Address"], ["hours", "Hours"],
  ["clerk_contact", "Clerk Contact"], ["filing_fees", "Filing Fees"], ["service_rules", "Service Requirements"],
  ["parking", "Parking & Logistics"], ["security", "Security & Access"],
];

export default function Courts() {
  const { user } = useAuth();
  const editor = isEditor(user?.role);
  const [courts, setCourts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/courts").then((r) => setCourts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm(BLANK); setOpen(true); };
  const openEdit = (c) => { setEditId(c.id); setForm({ ...BLANK, ...c }); setOpen(true); };

  const save = async () => {
    if (!form.name) return toast.error("Court name required");
    const payload = { ...form, image_url: form.image_url || DEFAULT_IMG };
    try {
      if (editId) { await api.put(`/courts/${editId}`, payload); toast.success("Court updated"); }
      else { await api.post("/courts", payload); toast.success("Court added"); }
      setOpen(false); setForm(BLANK); setEditId(null); load();
    } catch { toast.error("Could not save (editor access required)"); }
  };
  const remove = async (id) => { try { await api.delete(`/courts/${id}`); toast.success("Court removed"); load(); } catch { toast.error("Could not remove"); } };

  return (
    <div data-testid="courts-page">
      <PageHeader overline="Highest-Value Resource" title="Court & Jurisdiction Center"
        subtitle="Local rules, judge standing orders, e-filing steps, fee schedules and courthouse logistics for the courts in our service area.">
        {editor && <Button data-testid="add-court-btn" onClick={openNew}><Plus className="h-4 w-4 mr-1.5" /> Add Court</Button>}
      </PageHeader>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="courts-grid">
        {courts.map((c) => (
          <div key={c.id} className="group border border-border bg-card rounded-sm overflow-hidden hover:border-primary transition-colors animate-fade-up flex flex-col" data-testid="court-card">
            <div className="h-32 relative overflow-hidden bg-muted">
              <img src={c.image_url} alt={c.name} className="w-full h-full object-cover pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#051E36]/80 to-transparent" />
              <div className="absolute bottom-0 left-0 p-3">
                <Landmark className="h-4 w-4 text-primary mb-1" />
                <div className="text-white font-display font-bold text-sm leading-tight">{c.name}</div>
              </div>
              {editor && (
                <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid="edit-court-btn" onClick={() => openEdit(c)} className="bg-[#051E36]/70 text-white p-1.5 rounded-sm hover:bg-primary"><Pencil className="h-3.5 w-3.5" /></button>
                  <button data-testid="delete-court-btn" onClick={() => remove(c.id)} className="bg-[#051E36]/70 text-white p-1.5 rounded-sm hover:bg-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              )}
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <div className="text-xs text-muted-foreground flex items-center gap-1.5"><MapPin className="h-3 w-3" /> {c.jurisdiction}</div>
              <div className="text-xs text-muted-foreground mt-2 line-clamp-2">{c.efiling}</div>
              <div className="mt-4 flex items-center gap-3 pt-3 border-t border-border">
                <Link to={`/courts/${c.id}`} data-testid="court-details-link" className="text-sm font-medium text-primary flex items-center gap-1 hover:gap-2 transition-all">
                  Full resource <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                {c.website && (
                  <a href={c.website} target="_blank" rel="noreferrer" data-testid="court-website-link" className="ml-auto text-xs text-muted-foreground hover:text-primary flex items-center gap-1">
                    Official site <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="court-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit Court" : "Add Court"}</DialogTitle><DialogDescription className="sr-only">Court details form.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {FIELDS.map(([k, l]) => (
              <div key={k} className={["efiling", "service_rules", "filing_fees", "parking", "security"].includes(k) ? "col-span-2" : ""}>
                <Label className="text-xs">{l}</Label>
                {["efiling", "service_rules", "filing_fees", "parking", "security"].includes(k)
                  ? <Textarea rows={2} data-testid={`court-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                  : <Input data-testid={`court-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />}
              </div>
            ))}
          </div>
          <DialogFooter><Button data-testid="save-court-btn" onClick={save}>{editId ? "Save Changes" : "Add Court"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
